import type {
  AuditRecord,
  EditableUserProfile,
  ManagerSummary,
  MeResponse,
  ProfilePatch,
  UserListItem
} from "../types/peopleAdmin";

const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || "/api";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly details?: unknown
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export async function getMe(): Promise<MeResponse> {
  return request<MeResponse>("/me");
}

export async function searchUsers(division: string, search: string): Promise<UserListItem[]> {
  const query = new URLSearchParams({ division });
  if (search.trim()) {
    query.set("search", search.trim());
  }

  const response = await request<{ users: UserListItem[] }>(`/users?${query.toString()}`);
  return response.users;
}

export async function getUser(id: string): Promise<EditableUserProfile> {
  const response = await request<{ user: EditableUserProfile }>(`/users/${encodeURIComponent(id)}`);
  return response.user;
}

export async function patchUser(id: string, patch: ProfilePatch): Promise<void> {
  await request(`/users/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: {
      "content-type": "application/json"
    },
    body: JSON.stringify(patch)
  });
}

export async function uploadPhoto(id: string, file: File): Promise<void> {
  await request(`/users/${encodeURIComponent(id)}/photo`, {
    method: "PUT",
    headers: {
      "content-type": file.type
    },
    body: file
  });
}

export async function listManagers(division: string, search: string): Promise<ManagerSummary[]> {
  const query = new URLSearchParams({ division });
  if (search.trim()) {
    query.set("search", search.trim());
  }

  const response = await request<{ managers: ManagerSummary[] }>(`/managers?${query.toString()}`);
  return response.managers;
}

export async function listAudit(targetUserId: string): Promise<AuditRecord[]> {
  const query = new URLSearchParams({ targetUserId });
  const response = await request<{ records: AuditRecord[] }>(`/audit?${query.toString()}`);
  return response.records;
}

async function request<T = void>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    credentials: "include",
    ...init
  });

  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get("content-type") || "";
  const body = contentType.includes("application/json") ? await response.json() : await response.text();

  if (!response.ok) {
    const message = typeof body === "object" && body !== null && "error" in body ? String(body.error) : response.statusText;
    throw new ApiError(response.status, message, body);
  }

  return body as T;
}
